import { useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import Chip from '@mui/material/Chip'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { useNavigate } from 'react-router-dom'
import eventBus from "../../utils/eventBus"
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'
import CustomButton from 'components/Common/Button'

import { VaultAPI } from 'api'
import { Checkbox, FormControlLabel, Grid } from '@mui/material'
import { useState } from 'react'
import Snackbar from 'components/Common/SnackBar'

const sx = {
  header: {
    padding: '0 0 50px 0',

    h6: {
      color: '#25245F',
    },

    p: {
      color: '#66668F',
      marginTop: '10px',
    },
  },
  buttonPlacement: {
    marginTop: '30px',
    display: 'flex',
    justifyContent: 'end'
  },

  tableBox: {
    height: '500px',
    width: '100%',
  },

  table: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '10px 0',
    color: '#66668F',
    border: 'none',
    boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',

    '.MuiDataGrid-columnHeaders': {
      color: '#25245F',
      borderBottom: 'none',
    },

    '.MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
      paddingRight: '20px',
    },

    '.MuiDataGrid-columnHeaderTitle': {
      width: '100%',
      lineHeight: 'initial',
      borderBottom: '1px solid #25245F',
      fontSize: '12px',
    },

    '.MuiDataGrid-cell': {
      borderBottom: 'none',
      // justifyContent: 'center',
      whiteSpace: 'normal !important',
    },

    '.MuiDataGrid-cellContent': {
      paddingRight: '20px',
    },

    '.MuiChip-root': {
      fontSize: '12px',
      minWidth: '84px',
    },

    '.MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },

    '.highlight': {
      backgroundColor: '#F6FCFA',
    },
    '.MuiCheckbox-root': {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },

    // "& .MuiDataGrid-row:hover": {
    //     backgroundColor: 'none',
    //   },
  },

  verified: {
    backgroundColor: '#00B596',
    color: '#ffffff',

    '.MuiChip-icon': {
      color: '#ffffff',
    },
  },

  pending: {
    background: '#BDBDCE',
    color: '#fff',
  },

  buttonHolder: {
    marginTop: '30px',
  },
  buttonVault: {
    '.MuiTypography-root': {
      textTransform: 'initial',
    },
  },
}

export default function Ownership({ onDataChange }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [checkdValue, setCheckedValue] = useState({})
  const [saveDisable, setDisable] = useState(true)
  const [snackData, setSnackData] = useState({
    isOpen: false,
    type: 'success',
    message: ' ',
  })
  // console.log('🚀 ~ file: Ownership.js:128 ~ Ownership ~ snackData', snackData)

  const user = useSelector(state => state.userReducer.user)
  const businessId = user?.business?.find(b => b.is_default === 'True')?.business_id
  const BOs = useSelector(state => state.vaultReducer.BOs)

  const columns = [
    {
      field: 'type',
      headerName: 'OWNER TYPE',
      sortable: false,
      width: 130,
      valueGetter: params => (params.row.type === 'B' ? 'Business' : 'Individual'),
    },
    {
      field: 'first_name',
      headerName: 'NAME',
      sortable: false,
      width: 140,
      valueGetter: params =>
        params.row.type === 'B' ? params.row.business_name : params.row.first_name + ' ' + params.row.last_name,
    },
    {
      field: 'email_address',
      headerName: 'EMAIL',
      sortable: false,
      width: 260,
    },
    {
      field: 'created_on',
      headerName: 'EMAIL SENT ON',
      sortable: false,
      width: 190,
      valueGetter: params => moment(params.row.created_on).format('MM/DD/YYYY'),
    },
    {
      field: 'completed_on',
      headerName: 'FORM COMPLETED',
      sortable: false,
      width: 190,
      valueGetter: params => (params.row.completed_on ? moment(params.row.completed_on).format('MM/DD/YYYY') : null),
    },
    {
      field: 'status',
      headerName: 'STATUS',
      sortable: false,
      width: 120,
      renderCell: params => (
        <div>
          {params.value === 'P' ? (
            <Chip label="Pending" sx={sx.pending} />
          ) : (
            <Chip sx={sx.verified} icon={<CheckCircle />} label="Verified" />
          )}
        </div>
      ),
    },

    {
      field: 'display_in_directory',
      headerName: 'DISPLAY IN DIRECTORY',
      sortable: false,
      width: 180,
      renderCell: function ({ row }) {
        return (
          <FormControlLabel
            sx={sx.checkboxLabel}
            control={
              <Checkbox
                name={row.display_in_directory}
                defaultChecked={row.display_in_directory}
                onChange={e => onCheck(row.t_beneficial_owner_id, e)}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 20, marginLeft: 6 } }}
              />
            }
          />
        )
      },
    },
  ]

  const onCheck = (boId, e) => {
    setCheckedValue({ ...checkdValue, [boId]: e.target.checked })
    setDisable(false)
    onDataChange(true)
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line
  }, [businessId, dispatch])

  const loadData = useCallback(() => {
    VaultAPI.fetchingBOs(dispatch, null, businessId)
  }, [businessId, dispatch])

  const saveCallback = data => {
    setSnackData(data)
    setDisable(true)
  }

  const handleUpdate = useCallback(async () => {
    const check = Object.keys(checkdValue).map(item => {
      return { beneficialOwnerId: item, displayInDirectory: checkdValue[item] }
    })
    await VaultAPI.submitBOInformation(check, res => {
      if (res) {
        saveCallback(res)
        loadData()
      }
    })
    setDisable(true)
    onDataChange(false)
  }, [checkdValue, loadData, onDataChange])


  useEffect(() => {
    eventBus.on('cyd-tab-change', handleUpdate);
    return function () {
      eventBus.off('cyd-tab-change', handleUpdate);
    }

  }, [handleUpdate])


  function handleClick() {
    navigate('/vault-verification')
  }

  return (
    <Box>
      {BOs.length === 0 ? (
        <>
          <Box sx={sx.header}>
            <Typography variant="h6">Confirm/edit your Ownership information</Typography>
            <Typography variant="body2">
              This page shows the Beneficial Ownership information of your business. Each BO is required to fill out a
              form confirming their information. Once a Beneficial Owner completes the form, you will receive a
              confirmation email and see their status as "Verifed". Beneficial Owners can be managed directly in your
              Vault.
            </Typography>
          </Box>

          <Box>
            <CustomButton type={PRIMARY_BUTTON} sx={sx.buttonVault} onClick={handleClick}>
              Go to Vault to Add More Owners
            </CustomButton>
          </Box>
        </>
      ) : (
        <>
          <Grid container>
            <Grid item xs={10.5}>
              <Box sx={sx.header}>
                <Typography variant="h6">Confirm/edit your Ownership information</Typography>
                <Typography variant="body2">
                  This page shows the Beneficial Ownership information of your business. Each BO is required to fill out
                  a form confirming their information. Once a Beneficial Owner completes the form, you will receive a
                  confirmation email and see their status as "Verifed". Beneficial Owners can be managed directly in
                  your Vault.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={1.5} sx={sx.buttonPlacement}>
              <Box display="flex" justifyContent="flex-end">
                <CustomButton
                  sx={{ marginBottom: '40px' }}
                  type={saveDisable ? DISABLED_BUTTON : PRIMARY_BUTTON}
                  onClick={handleUpdate}
                >
                  Update
                </CustomButton>
              </Box>
            </Grid>
          </Grid>

          <Box sx={sx.tableBox}>
            <DataGrid
              rowHeight={100}
              sx={sx.table}
              getRowId={row => row.t_beneficial_owner_id}
              rows={BOs}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5]}
              disableSelectionOnClick
              disableColumnMenu={true}
              getRowClassName={params => (params.row.status === 'C' ? 'highlight' : '')}
            />
          </Box>
          <Box sx={sx.buttonHolder}>
            <CustomButton type={PRIMARY_BUTTON} onClick={handleClick}>
              Go to Vault to Add More Owners
            </CustomButton>
          </Box>
          {snackData.isOpen && <Snackbar isOpen={true} type={snackData.type} message={snackData.message} />}
        </>
      )}
    </Box>
  )
}
